import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  DollarSign, 
  TrendingUp,
  Calendar,
  Building2,
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  Receipt,
  CreditCard,
  Info,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function PartnerBilling() {
  const { partner } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [upcomingPricing, setUpcomingPricing] = useState([]);

  useEffect(() => {
    fetchBilling();
    fetchUpcomingPricing();
  }, []);

  const fetchBilling = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await axios.get(`${API_URL}/api/partner/billing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBilling(response.data);
    } catch (error) {
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingPricing = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/billing/pricing/upcoming`);
      setUpcomingPricing(response.data.upcoming || []);
    } catch (error) {
      console.error('Failed to fetch upcoming pricing:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/30">Paid</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30">Pending</Badge>;
      case 'overdue':
        return <Badge className="bg-red-500/10 text-red-600 border-red-500/30">Overdue</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Billing</h1>
        <p className="text-muted-foreground mt-1">
          Manage your wholesale billing and view invoices
        </p>
      </div>

      {/* Upcoming Price Change Alert */}
      {upcomingPricing.length > 0 && (
        <Alert className="mb-6 bg-amber-500/10 border-amber-500/30">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertTitle className="text-amber-600 font-semibold">Upcoming Price Change</AlertTitle>
          <AlertDescription className="text-amber-600/90">
            {upcomingPricing.map((version, idx) => (
              <div key={idx} className="mt-1">
                <strong>Effective {formatDate(version.effective_date)}:</strong> Pro plan wholesale price will change to{' '}
                <span className="font-semibold">{formatCurrency(version.prices?.pro?.wholesale_price_monthly || 0)}/user</span>.
                Your costs will update at your next billing cycle after this date.
                {version.notes && <span className="block text-sm mt-1 opacity-80">{version.notes}</span>}
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* Info Alert */}
      <Alert className="mb-6 bg-blue-500/5 border-blue-500/30">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-600">
          As a partner, you receive wholesale pricing. You bill your customers directly at your own rates.
        </AlertDescription>
      </Alert>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-cyan-600">
              Monthly Cost
            </CardTitle>
            <Receipt className="w-4 h-4 text-cyan-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-cyan-600">
              {formatCurrency(billing?.current_month?.total_cost || 0)}
            </div>
            <p className="text-xs text-cyan-600/70 mt-1">
              {billing?.current_month?.total_users || 0} Pro users @ wholesale rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pro Users
            </CardTitle>
            <Users className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {billing?.current_month?.total_users || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Across all customers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Next Invoice
            </CardTitle>
            <Calendar className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              1st
            </div>
            <p className="text-xs text-muted-foreground mt-1">of next month</p>
          </CardContent>
        </Card>
      </div>

      {/* Wholesale Rates */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Wholesale Rates</CardTitle>
          <CardDescription>
            Your cost per user - set your own retail prices for profit margin
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {billing?.pricing_info && Object.entries(billing.pricing_info).map(([plan, info]) => (
              <div 
                key={plan} 
                className={`p-4 rounded-lg border ${
                  plan === 'pro' 
                    ? 'bg-cyan-500/5 border-cyan-500/30' 
                    : 'bg-secondary/50'
                }`}
              >
                <h4 className="font-medium capitalize mb-2">{plan} Plan</h4>
                <div className="space-y-2">
                  <div>
                    <p className="text-2xl font-bold text-primary">
                      {formatCurrency(info.wholesale_price)}
                    </p>
                    <p className="text-xs text-muted-foreground">Your cost per user/month</p>
                  </div>
                  {info.retail_price > 0 && (
                    <div className="pt-2 border-t border-border/50">
                      <p className="text-sm text-muted-foreground">
                        Suggested retail: {formatCurrency(info.retail_price)}/user
                      </p>
                      <p className="text-xs text-green-600">
                        Potential margin: {formatCurrency(info.suggested_margin)}/user
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Current Month Breakdown */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Current Month Breakdown</CardTitle>
          <CardDescription>
            Costs by customer - this will appear on your invoice
          </CardDescription>
        </CardHeader>
        <CardContent>
          {billing?.current_month?.breakdown?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {billing.current_month.breakdown.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        {item.company_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="capitalize">{item.plan}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        {item.user_count}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatCurrency(item.wholesale_price_per_user)}/user
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(item.monthly_cost)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-secondary/30">
                  <TableCell colSpan={4} className="font-medium">
                    Total Monthly Cost
                  </TableCell>
                  <TableCell className="text-right font-bold text-primary">
                    {formatCurrency(billing.current_month.total_cost)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No billable customers yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add customers with Pro plans to see billing details
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice History */}
      <Card>
        <CardHeader>
          <CardTitle>Invoice History</CardTitle>
          <CardDescription>
            Your past invoices and payment status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {billing?.invoice_history?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {billing.invoice_history.map((invoice) => (
                  <TableRow key={invoice.id || invoice.invoice_number}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-muted-foreground" />
                        {invoice.invoice_number}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(invoice.billing_period?.start)} - {formatDate(invoice.billing_period?.end)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatCurrency(invoice.total)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(invoice.status)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(invoice.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Receipt className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No invoices yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Invoices are generated on the 1st of each month
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
